/** SUNTRONIX ONE — Devices tab (Company detail).
 *
 *  Reused on both the Master Customer 360 (masterClientId → roll-up across
 *  every subsidiary) and the subsidiary Technology Environment page
 *  (masterCompanyId → single subsidiary's devices).
 *
 *  Columns: Asset Tag, Serial, Product, Category, State, Grade, Country,
 *  Warranty End.  Row → jumps back to the Asset Registry filtered by the
 *  device's serial so the operator lands on the full record.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";
import Section from "@/components/app/Section";
import {
  Boxes, ArrowRight, ExternalLink, Search, X, Upload, ChevronRight,
  Download, ShieldAlert,
} from "lucide-react";

const STATE_LABELS = {
  in_use: "In Use",
  in_warehouse: "In Warehouse",
  available: "Available",
  ready_to_deploy: "Ready to Deploy",
  reserved: "Reserved",
  in_transit: "In Transit",
  shipped: "Shipped",
  packed: "Packed",
  assigned: "Assigned",
  under_repair: "Under Repair",
  awaiting_inspection: "Awaiting Inspection",
  disposed: "Disposed",
  recycled: "Recycled",
  destroyed: "Destroyed",
  retired: "Retired",
  deactivated: "Deactivated",
};

const STATE_ORDER = [
  "in_use", "in_warehouse", "available", "ready_to_deploy",
  "reserved", "in_transit", "shipped", "packed",
  "under_repair", "awaiting_inspection", "retired", "disposed",
];

function labelForState(s) {
  return STATE_LABELS[s] || (s || "unknown").replace(/_/g, " ");
}

// Warranty severity for a device row.
//   "expired"  → past today            → red
//   "expiring" → within 60 days        → amber
//   "ok"       → later than 60 days    → default
//   null       → warranty_end unknown  → muted
function warrantySeverity(warrantyEnd) {
  if (!warrantyEnd) return null;
  const end = new Date(warrantyEnd);
  if (Number.isNaN(end.getTime())) return null;
  const now = new Date();
  const diffDays = Math.floor((end - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays <= 60) return "expiring";
  return "ok";
}

export default function DevicesTab({ masterClientId, masterCompanyId, scopeLabel }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total: 0, by_state: {} });
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("all");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [warrantyFilter, setWarrantyFilter] = useState("all"); // all | expiring | expired
  const [exporting, setExporting] = useState(false);
  const PAGE = 25;

  const scopeParams = useMemo(() => {
    const p = {};
    if (masterClientId) p.master_client_id = masterClientId;
    if (masterCompanyId) p.master_company_id = masterCompanyId;
    return p;
  }, [masterClientId, masterCompanyId]);

  // Load the summary strip + option pools.  Cheap aggregate call scoped to
  // this master client / subsidiary so the numbers match the table below.
  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/assets/state-summary", { params: scopeParams });
      setSummary(data);
    } catch { setSummary({ total: 0, by_state: {} }); }
  }, [scopeParams]);

  // Load rows using the standard /assets endpoint so the tenant filter,
  // Suntronix-only fields, and Preflight guards stay consistent.
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...scopeParams,
        limit: PAGE,
        skip,
      };
      if (q) params.q = q;
      if (state !== "all") params.lifecycle_state = state;
      if (category !== "all") params.category = category;
      if (country !== "all") params.country = country;
      const { data } = await api.get("/assets/", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      // Distincts drawn from the current page so the Select stays useful
      // without a separate roundtrip.  If the operator picks one, the
      // server-side filter takes over.  Functional updates so we don't
      // have to depend on the previous option arrays.
      const items = data.items || [];
      setCategoryOptions((prev) => {
        const s = new Set(prev);
        items.forEach((a) => a.category && s.add(a.category));
        return Array.from(s).sort();
      });
      setCountryOptions((prev) => {
        const s = new Set(prev);
        items.forEach((a) => a.country && s.add(a.country));
        return Array.from(s).sort();
      });
    } finally { setLoading(false); }
  }, [scopeParams, skip, q, state, category, country]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const clearFilters = () => {
    setQ(""); setState("all"); setCategory("all"); setCountry("all");
    setWarrantyFilter("all"); setSkip(0);
  };

  const openInRegistry = (asset) => {
    const query = asset.serial || asset.asset_tag;
    navigate(`/assets?q=${encodeURIComponent(query)}`);
  };

  // CSV export — tenant-scoped, works for customer admins.  Same
  // filters as the visible table so what you see is what you get.
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (masterClientId)  params.set("master_client_id", masterClientId);
      if (masterCompanyId) params.set("master_company_id", masterCompanyId);
      if (q)                 params.set("q", q);
      if (state !== "all")   params.set("lifecycle_state", state);
      if (category !== "all")params.set("category", category);
      if (country !== "all") params.set("country", country);
      const url = process.env.REACT_APP_BACKEND_URL
        + "/api/assets/devices-export.csv"
        + (params.toString() ? "?" + params.toString() : "");
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Export failed");
      const rows = r.headers.get("X-Row-Count") || "?";
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, "");
      a.download = `SUNTRONIX_ONE_Devices_${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Exported ${rows} devices`);
    } catch {
      toast.error("Could not export devices");
    } finally { setExporting(false); }
  };

  // Sub-tenant roll-up — ZIP of one CSV per subsidiary + a manifest.
  // Only meaningful on the Master Customer 360 view (masterClientId set).
  const exportZipPack = async () => {
    if (exporting || !masterClientId) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("master_client_id", masterClientId);
      if (country !== "all") params.set("country", country);
      if (category !== "all")params.set("category", category);
      if (state !== "all")   params.set("lifecycle_state", state);
      const url = process.env.REACT_APP_BACKEND_URL
        + "/api/assets/devices-export-pack.zip?" + params.toString();
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Pack export failed");
      const subs = r.headers.get("X-Subsidiary-Count") || "?";
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, "");
      a.download = `SUNTRONIX_ONE_Devices_Pack_${stamp}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Packed ${subs} subsidiaries`);
    } catch {
      toast.error("Could not export ZIP pack");
    } finally { setExporting(false); }
  };

  // Filter rows client-side for the warranty chip.  Server doesn't
  // need to know about "expiring 60d" — it's a UX slice.
  const visibleRows = useMemo(() => {
    if (warrantyFilter === "all") return rows;
    return rows.filter((a) => {
      const sev = warrantySeverity(a.warranty_end);
      if (warrantyFilter === "expiring") return sev === "expiring" || sev === "expired";
      if (warrantyFilter === "expired")  return sev === "expired";
      return true;
    });
  }, [rows, warrantyFilter]);

  const expiringCount = useMemo(
    () => rows.filter((a) => {
      const sev = warrantySeverity(a.warranty_end);
      return sev === "expiring" || sev === "expired";
    }).length,
    [rows],
  );

  // Empty state — nothing linked at all yet.  We surface an "Assign Devices"
  // action that jumps into the Registry with a pre-filter so operators can
  // create / bulk-upload devices directly for this company.
  if (!loading && summary.total === 0 && !q && state === "all"
      && category === "all" && country === "all") {
    return (
      <Section title="Devices"
        hint={`No devices are linked to ${scopeLabel || "this company"} yet.`}
        testId="devices-empty">
        <div className="st-panel p-8 text-center">
          <Boxes className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
          <div className="mt-3 text-[15px] font-medium text-slate-800">
            No devices linked yet
          </div>
          <p className="mt-1 text-[13px] text-slate-500 max-w-md mx-auto">
            When devices are created or bulk uploaded against
            {" "}<b>{scopeLabel || "this company"}</b>, they will appear here as
            part of the Golden Asset Record.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Button
              data-testid="devices-assign-btn"
              onClick={() => {
                const p = new URLSearchParams();
                if (masterCompanyId) p.set("master_company_id", masterCompanyId);
                navigate(`/assets${p.toString() ? "?" + p : ""}`);
              }}>
              <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
              Assign Devices
            </Button>
            <Button variant="outline"
              onClick={() => navigate("/assets")}
              data-testid="devices-open-registry-btn">
              Open Asset Registry
              <ChevronRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.6} />
            </Button>
          </div>
        </div>
      </Section>
    );
  }

  // Summary strip — total + top states as tap-through chips.  Clicking a
  // chip filters the table.  Only render chips for states that actually
  // exist so the header stays compact.
  const chips = STATE_ORDER
    .filter((s) => summary.by_state?.[s])
    .map((s) => ({ state: s, count: summary.by_state[s] }));

  const pages = Math.max(1, Math.ceil(total / PAGE));
  const page = Math.floor(skip / PAGE) + 1;

  return (
    <div className="space-y-4" data-testid="devices-tab">
      {/* Summary strip */}
      <div className="st-panel p-4" data-testid="devices-summary">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-2xl font-heading font-semibold text-slate-900">
            {summary.total}
          </span>
          <span className="text-[13px] text-slate-500">total</span>
          {chips.map((c) => (
            <button key={c.state}
              onClick={() => { setState(c.state); setSkip(0); }}
              data-testid={`devices-chip-${c.state}`}
              className={
                "ml-2 px-2 py-0.5 rounded border text-[12px] transition-colors " +
                (state === c.state
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400")
              }>
              <span className="font-mono tabular-nums font-semibold">{c.count}</span>
              <span className="ml-1">{labelForState(c.state)}</span>
            </button>
          ))}
          {expiringCount > 0 && (
            <button
              onClick={() => setWarrantyFilter(
                warrantyFilter === "expiring" ? "all" : "expiring")}
              data-testid="devices-chip-expiring"
              className={
                "ml-2 px-2 py-0.5 rounded border text-[12px] transition-colors inline-flex items-center gap-1 " +
                (warrantyFilter === "expiring"
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400")
              }>
              <ShieldAlert className="h-3 w-3" strokeWidth={1.8} />
              <span className="font-mono tabular-nums font-semibold">{expiringCount}</span>
              <span>Warranty ≤ 60d</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="st-panel p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.6} />
          <Input className="pl-8"
            placeholder="Search serial, tag, IMEI, model, SKU…"
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            data-testid="devices-search" />
        </div>
        <Select value={state} onValueChange={(v) => { setSkip(0); setState(v); }}>
          <SelectTrigger className="h-9 w-44" data-testid="devices-filter-state">
            <SelectValue placeholder="State" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            {STATE_ORDER.map((s) => (
              <SelectItem key={s} value={s}>{labelForState(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => { setSkip(0); setCategory(v); }}>
          <SelectTrigger className="h-9 w-44" data-testid="devices-filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categoryOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={country} onValueChange={(v) => { setSkip(0); setCountry(v); }}>
          <SelectTrigger className="h-9 w-40" data-testid="devices-filter-country">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All countries</SelectItem>
            {countryOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(q || state !== "all" || category !== "all" || country !== "all"
              || warrantyFilter !== "all") && (
          <Button variant="ghost" size="sm" onClick={clearFilters}
            data-testid="devices-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {masterClientId && (
            <Button variant="outline" size="sm" onClick={exportZipPack}
              disabled={exporting}
              data-testid="devices-export-pack-btn"
              title="Export one CSV per subsidiary in a single ZIP">
              <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.6} />
              Export ZIP Pack
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportCsv}
            disabled={exporting}
            data-testid="devices-export-btn">
            <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.6} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="st-panel overflow-x-auto" data-testid="devices-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[color:var(--st-surface-alt)] text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
            <tr>
              <th className="text-left px-3 py-2">Asset Tag</th>
              <th className="text-left px-3 py-2">Serial</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Grade</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Warranty End</th>
              <th className="text-right px-3 py-2 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="p-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan={9} className="p-6 text-center text-slate-400">
                  No devices match the current filters.
                </td>
              </tr>
            )}
            {!loading && visibleRows.map((a) => {
              const sev = warrantySeverity(a.warranty_end);
              const warrantyCls =
                sev === "expired"  ? "text-red-700 font-semibold bg-red-50"
                : sev === "expiring" ? "text-amber-800 font-semibold bg-amber-50"
                : "text-slate-700";
              return (
              <tr key={a.id}
                onClick={() => openInRegistry(a)}
                className="border-t border-[color:var(--st-border)] hover:bg-[color:var(--st-surface-alt)] cursor-pointer"
                data-testid={`devices-row-${a.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{a.asset_tag || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{a.serial || "—"}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">
                    {[a.brand, a.model].filter(Boolean).join(" ") || a.product || "—"}
                  </div>
                  {a.config && <div className="text-[11px] text-slate-500">{a.config}</div>}
                </td>
                <td className="px-3 py-2">{a.category || "—"}</td>
                <td className="px-3 py-2"><StatusBadge value={a.lifecycle_state} /></td>
                <td className="px-3 py-2">
                  {a.condition
                    ? <StatusBadge value={a.condition} />
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2">{a.country || "—"}</td>
                <td className={"px-3 py-2 font-mono text-[11px] " + warrantyCls}
                    data-testid={`devices-warranty-${a.id}`}>
                  <span className="inline-flex items-center gap-1">
                    {(sev === "expired" || sev === "expiring") && (
                      <ShieldAlert className="h-3 w-3" strokeWidth={1.8} />
                    )}
                    {a.warranty_end || "—"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-slate-400">
                  <ExternalLink className="h-3.5 w-3.5 inline-block" strokeWidth={1.6} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div className="flex items-center justify-between text-[12px] text-slate-500"
          data-testid="devices-pagination">
          <span>{total} devices · Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE))}
              data-testid="devices-prev">Previous</Button>
            <Button size="sm" variant="outline"
              disabled={skip + PAGE >= total}
              onClick={() => setSkip(skip + PAGE)}
              data-testid="devices-next">Next</Button>
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-400 flex items-center gap-1">
        Devices link back to the Asset Registry — click any row to open the
        full record.  <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
      </div>
    </div>
  );
}
